import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import type { OrgInvitation } from '../types/org-users.types';

interface RevokeInvitationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitation: OrgInvitation | null;
  onConfirm: () => Promise<void>;
}

export const RevokeInvitationDialog = ({
  open,
  onOpenChange,
  invitation,
  onConfirm,
}: RevokeInvitationDialogProps) => {
  const { t } = useTranslation();
  const [isRevoking, setIsRevoking] = useState(false);

  const handleConfirm = async () => {
    setIsRevoking(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Error handled in parent
    } finally {
      setIsRevoking(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isRevoking) {
      onOpenChange(newOpen);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-[#fff] w-[95vw] max-w-md max-h-[90dvh] p-4 sm:p-6 rounded-2xl">
        <AlertDialogHeader className="text-left">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--crimson))]/10 shrink-0">
              <AlertTriangle className="size-5 text-[hsl(var(--crimson))]" />
            </div>
            <AlertDialogTitle className="text-base sm:text-lg">
              {t('ORGTEAM_REVOKEDIALOG_TITLE')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            {t('ORGTEAM_REVOKEDIALOG_DESCRIPTION', {
              email: invitation?.email || '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isRevoking}>
            {t('ORGTEAM_REVOKEDIALOG_ACTIONS_CANCEL')}
          </Button>
          <Button size={'sm'} variant="destructive" onClick={handleConfirm} disabled={isRevoking}>
            {isRevoking && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('ORGTEAM_REVOKEDIALOG_ACTIONS_REVOKE')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
